import React, { useState } from 'react';
import { App, Badge, Card, Empty, Space, Spin, Tag, Tooltip, Typography } from 'antd';
import { DragDropContext, Draggable, Droppable, type DropResult } from '@hello-pangea/dnd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import ReasonPrompt from './shared/ReasonPrompt';
import { APPLICATION_NEXT, recruitErrorMessage, stageLabel } from './shared/recruitUi';

const { Text } = Typography;

const BOARD_STAGES = ['Applied', 'Screening', 'Interview', 'Offer', 'Hired', 'TalentPool', 'Rejected', 'Withdrawn'];

const STAGE_ACCENT: Record<string, string> = {
  Applied: '#1677ff',
  Screening: '#13c2c2',
  Interview: '#722ed1',
  Offer: '#fa8c16',
  Hired: '#52c41a',
  TalentPool: '#2f54eb',
  Rejected: '#ff4d4f',
  Withdrawn: '#8c8c8c',
};

interface Props {
  filters: { job_posting_id?: string; owner_employee_id?: string; department_id?: string; q?: string };
}

/** Kanban pipeline. Cards per column are capped server-side; column badges show the true totals. */
const ApplicationBoard: React.FC<Props> = ({ filters }) => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [pendingReject, setPendingReject] = useState<string | null>(null);

  const key = hrmsKeys.applicationBoard(filters);
  const { data: board, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => hrmsService.getApplicationBoard(filters),
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: hrmsKeys.applicationBoard() });
    await queryClient.invalidateQueries({ queryKey: hrmsKeys.applications() });
  };

  const move = async (id: string, to: string, extra: object = {}) => {
    try {
      await hrmsService.updateApplicationStatus(id, { status: to, ...extra });
      message.success(`Moved to ${stageLabel(to)}`);
    } catch (e: any) {
      message.error(recruitErrorMessage(e, 'Move failed'));
    } finally {
      await refresh();
    }
  };

  const onDragEnd = (r: DropResult) => {
    if (!r.destination) return;
    const from = r.source.droppableId;
    const to = r.destination.droppableId;
    if (from === to) return;
    if (to === 'Hired') {
      message.info('Hiring happens by converting an accepted offer');
      return;
    }
    if (!APPLICATION_NEXT[from]?.includes(to)) {
      message.warning(`Cannot move from ${stageLabel(from)} to ${stageLabel(to)}`);
      return;
    }
    if (to === 'Rejected') {
      setPendingReject(r.draggableId);
      return;
    }
    void move(r.draggableId, to);
  };

  if (isLoading) return <Spin />;
  if (!board) return <Empty />;

  return (
    <>
      <DragDropContext onDragEnd={onDragEnd}>
        <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 12, alignItems: 'flex-start' }}>
          {BOARD_STAGES.map((stage) => {
            const col = board[stage] || { total: 0, cards: [] };
            return (
              <Droppable droppableId={stage} key={stage}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    style={{
                      minWidth: 250,
                      width: 250,
                      background: snapshot.isDraggingOver ? '#e6f4ff' : '#fafafa',
                      borderRadius: 8,
                      borderTop: `3px solid ${STAGE_ACCENT[stage]}`,
                      padding: 8,
                      maxHeight: 'calc(100vh - 280px)',
                      overflowY: 'auto',
                    }}
                  >
                    <Space style={{ marginBottom: 8, width: '100%', justifyContent: 'space-between' }}>
                      <Text strong>{stageLabel(stage)}</Text>
                      <Badge count={col.total} showZero overflowCount={9999} color={STAGE_ACCENT[stage]} />
                    </Space>
                    {col.cards.map((a: any, idx: number) => (
                      <Draggable draggableId={a.id} index={idx} key={a.id} isDragDisabled={stage === 'Hired'}>
                        {(p) => (
                          <div ref={p.innerRef} {...p.draggableProps} {...p.dragHandleProps} style={{ ...p.draggableProps.style, marginBottom: 8 }}>
                            <Card size="small" hoverable onClick={() => navigate(`/hr/applications/${a.id}`)}>
                              <Text strong ellipsis style={{ display: 'block' }}>
                                {a.candidate_name}
                              </Text>
                              <Text type="secondary" ellipsis style={{ display: 'block', fontSize: 12 }}>
                                {a.job_title}
                              </Text>
                              <Space size={4} wrap style={{ marginTop: 4 }}>
                                <Tag style={{ fontSize: 11 }}>{a.application_number}</Tag>
                                {a.screening_score != null && <Tag color="blue">{Number(a.screening_score)}</Tag>}
                                <Tooltip title="Days in this stage">
                                  <Tag color={Number(a.days_in_stage) > 7 ? 'orange' : 'default'}>{a.days_in_stage ?? 0}d</Tag>
                                </Tooltip>
                              </Space>
                              {a.owner_name && (
                                <Text type="secondary" style={{ display: 'block', fontSize: 11, marginTop: 2 }}>
                                  Owner: {a.owner_name}
                                </Text>
                              )}
                            </Card>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    {col.total > col.cards.length && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        +{col.total - col.cards.length} more — use list view to see all
                      </Text>
                    )}
                  </div>
                )}
              </Droppable>
            );
          })}
        </div>
      </DragDropContext>
      <ReasonPrompt
        open={Boolean(pendingReject)}
        title="Reject application"
        okText="Reject"
        appliesTo="Application"
        showNotify
        onCancel={() => setPendingReject(null)}
        onSubmit={async (v) => {
          const id = pendingReject!;
          setPendingReject(null);
          await move(id, 'Rejected', {
            rejection_reason_code: v.reason_code,
            rejection_notes: v.notes,
            notify_candidate: v.notify_candidate,
          });
        }}
      />
    </>
  );
};

export default ApplicationBoard;
